import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Form,
  Alert,
  Space,
  Typography,
  message,
  Switch,
  Divider,
  Select,
  InputNumber,
} from "antd";
import {
  PodcastConfig,
  UpdatePodcastConfigRequest,
  SPEAKER_TYPES,
  normalizePodcastConfig,
} from "../../../types/podcast";
import request from "../../../utils/request";
import { useGlobal } from "../../../contexts/GlobalContext";

const { Text } = Typography;

interface PodcastConfigModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const PodcastConfigModal: React.FC<PodcastConfigModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { API_BASE_URL, token } = useGlobal();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<PodcastConfig | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/podcasts/config`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        const configData = normalizePodcastConfig(response.data.data);
        setConfig(configData);
        form.setFieldsValue(configData);
      }
    } catch (error) {
      console.error("获取配置失败:", error);
    }
  }, [API_BASE_URL, token, form]);

  // 加载配置
  useEffect(() => {
    if (visible) {
      fetchConfig();
    }
  }, [visible, fetchConfig]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const params: UpdatePodcastConfigRequest = {
        speaker_type: values.speaker_type,
        audio_format: values.audio_format,
        use_head_music: values.use_head_music,
        use_tail_music: values.use_tail_music,
        speech_rate: values.speech_rate,
        language: values.language,
      };

      const response = await request.put(
        `${API_BASE_URL}/api/v3/podcasts/config`,
        params,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success("配置保存成功");
        onSuccess();
        onCancel();
      } else {
        message.error(response.data.msg || "配置保存失败");
      }
    } catch (error: any) {
      console.error("保存配置失败:", error);
      message.error(error.response?.data?.msg || "配置保存失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="AI播客配置"
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={680}
      destroyOnClose
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: "calc(100vh - 200px)",
          overflow: "auto",
        },
      }}
    >
      <Form form={form} layout="vertical">
        <Alert
          message="配置说明"
          description={
            <Space direction="vertical" size="small">
              <Text>
                AI播客会根据当前账户配置生成语音播客，支持发音人、语速、语言和音频格式调整。
              </Text>
              <Text type="secondary">
                这些配置会作为新播客生成时的默认值，保存后会立即生效。
              </Text>
            </Space>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form.Item
          name="speaker_type"
          label="发音人类型"
          rules={[{ required: true, message: "请选择发音人类型" }]}
        >
          <Select
            options={Object.entries(SPEAKER_TYPES).map(([key, label]) => ({
              value: key,
              label,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="audio_format"
          label="音频格式"
          rules={[{ required: true, message: "请选择音频格式" }]}
        >
          <Select
            options={[
              { value: "mp3", label: "MP3" },
              { value: "ogg_opus", label: "OGG Opus" },
              { value: "aac", label: "AAC" },
              { value: "pcm", label: "PCM" },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="language"
          label="语言"
          rules={[{ required: true, message: "请选择播客语言" }]}
        >
          <Select
            options={[
              { value: "zh-CN", label: "简体中文" },
              { value: "en-US", label: "English" },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="speech_rate"
          label="语速"
          rules={[{ required: true, message: "请设置语速" }]}
        >
          <InputNumber min={-50} max={100} style={{ width: "100%" }} />
        </Form.Item>

        <Divider />

        <Form.Item
          name="use_head_music"
          label="片头音效"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="use_tail_music"
          label="片尾音效"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        {config && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            当前默认配置：{SPEAKER_TYPES[config.speaker_type as keyof typeof SPEAKER_TYPES] || config.speaker_type} / {config.audio_format.toUpperCase()} / {config.language}
          </Text>
        )}
      </Form>
    </Modal>
  );
};

export default PodcastConfigModal;
